import { useEffect, useRef, useState, type InputHTMLAttributes } from "react";
import { css, cx } from "@styled-system/css";
import { Box } from "@styled-system/jsx";
import type { SystemProperties } from "@styled-system/types";
import VisibityOn from "@/assets/common/visibility_on.svg?react";
import VisibityOff from "@/assets/common/visibility_off.svg?react";
import { IconAsButton } from "./IconAsButton";

type Props = {
  label: string;
  attributes?: InputHTMLAttributes<HTMLInputElement>;
  name?: string;
  value?: string | number | null;
  error?: boolean;
  onInput?: (event: React.FormEvent<HTMLInputElement> & { target: HTMLInputElement }) => void;
  className?: string;
  style?: {
    bg?: SystemProperties["backgroundColor"];
  };
  /** Usefill for filling the input with a value on load from autocomplete */
  dispatchEventOnLoad?: boolean;
};

export default function Input({
  label,
  attributes,
  name = label,
  value,
  onInput,
  error,
  style = { bg: "surface" },
  className,
  dispatchEventOnLoad
}: Props) {
  const { bg } = style;
  const ref = useRef<HTMLInputElement>(null);
  const [forcedType, setForcedType] = useState(attributes?.type);

  useEffect(() => {
    const input = ref.current;
    if (input && dispatchEventOnLoad && !value) {
      input.dispatchEvent(new Event("input", { bubbles: true }));
      onInput && onInput({ target: input } as any);
    }
  }, []);

  return (
    <Box
      data-has-data={value ? "true" : "false"}
      className={cx(
        "textfield-container",
        css({
          width: "100%",
          '&[data-has-data="true"] span': {
            transform: "translate(0%, -70%) !important"
          },
          "&:focus-within span": {
            transform: "translate(0%, -70%)",
            color: "primary"
          },
          "&:focus-within input": {
            borderColor: "primary"
          }
        }),
        className
      )}
    >
      <label htmlFor={name} className={css({ position: "relative" })}>
        <span
          className={css({
            transition: "transform 0.2s, color 0.2s",
            ml: "19px",
            pl: "5px",
            pr: "5px",
            color: error ? "warning" : "lowEmphasis",
            position: "absolute",
            bg,
            left: "0px",
            bottom: "50%",
            transform: "translate(0%, 50%)",
            pointerEvents: "none",
            _focus: {
              color: error ? "warning" : "primary"
            }
          })}
        >
          {label}
        </span>
        <input
          ref={ref}
          value={value || ""}
          onInput={onInput}
          name={name}
          {...attributes}
          type={forcedType || attributes?.type}
          className={cx(
            "textfield",
            attributes?.className,
            css({
              width: "100%",
              transition: "all 0.2s",
              height: "64px",
              mdDown: { height: "56px" },
              bg,
              px: "24px",
              outline: "none",
              color: "highEmphasis",
              border: "2px solid",
              _hover: {
                borderColor: error ? "warning" : "mediumEmphasis"
              },
              "&:-webkit-autofill": {
                // backgroundClip: "padding-box",
                boxShadow: "0 0 0 30px var(--colors-surface) inset!important", // use bg prop? is needed?
                boxShadowColor: bg as SystemProperties["boxShadowColor"]
                // WebkitTextFillColor: "var(--colors-high-emphasis)"
                //   caretColor: "highEmphasis",
                //   _hover: {},
                //   _focus: {},
                //   _active: {}
              },
              borderColor: error ? "warning" : "lowEmphasis",
              borderRadius: "32px"
            })
          )}
        />
        {attributes?.type === "password" && (
          <IconAsButton
            onClick={() => setForcedType(forcedType === "text" ? "password" : "text")}
            Icon={forcedType === "password" ? VisibityOn : VisibityOff}
            className={css({ position: "absolute", top: "50%", transform: "translateY(-50%)", right: "16px" })}
          />
        )}
      </label>
    </Box>
  );
}
